import { z } from 'zod';
import { API_BASE, apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * REJESTR AGENTÓW — kto się zgłosił z maszyn klienta i czy go wpuszczamy.
 *
 * ⚠️ `acceptance` przychodzi WYLICZONE z serwera i front go nie odtwarza. Reguła („puste kolumny
 * znaczą czeka, odrzucenie wygrywa nad potwierdzeniem") bramkuje ingest, więc jej druga kopia
 * w przeglądarce mogłaby pokazywać coś innego, niż portal faktycznie robi z danymi.
 */

export const ACCEPTANCE = ['POTWIERDZONY', 'OCZEKUJE', 'ODRZUCONY', 'NIEZNANY'] as const;

/**
 * STANY ZADANIA MASZYNOWEGO.
 *
 * ⚠️ DEKLARACJA STOI PRZED PIERWSZYM UŻYCIEM, i to nie jest kosmetyka: schematy zod powstają przy
 * wczytaniu modułu, więc odwołanie do stałej zadeklarowanej NIŻEJ wywala `ReferenceError` przy
 * starcie aplikacji, a nie przy pierwszym żądaniu. Złapał to `tsc` (TS2448), gdy wiersz agenta
 * dostał stan ostatniego wyłączenia replikacji.
 */
export const MACHINE_TASK_STATUS = ['QUEUED', 'SENT', 'RUNNING', 'DONE', 'FAILED'] as const;

const AgentRowSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  kind: z.enum(['DATABASE', 'MACHINE']),
  version: z.string().nullable(),
  enrolledAt: z.string(),
  lastSeenAt: z.string().nullable(),
  /** POWÓD niepowodzenia poprzedniego cyklu — `null` = ostatni cykl się udał. */
  lastProblem: z.string().nullable(),
  lastProblemAt: z.string().nullable(),
  /**
   * KANAŁ WYDAŃ RODZEŃSTWA — stan per węzeł.
   *
   * ⚠️⚠️ ZE ZMIERZONEJ AWARII (testówka PESA, 26.08.2026): kanał nie wpisał ani jednego wiersza
   * przez pół dnia, a ten ekran pokazywał cztery zdrowe węzły. `wydaniaDolPytalO` jest tu
   * najważniejsze — tylko ono rozdziela „pyta i nie ma czego wpisywać" od „nie pyta wcale".
   *
   * `optional`, bo starsza instancja tych pól nie odda.
   */
  wydaniaDolSeq: z.number().nullable().optional(),
  wydaniaDolSzczyt: z.number().optional(),
  wydaniaDolWpisanych: z.number().optional(),
  /**
   * Operacje konfiguracji, które ten węzeł ma jeszcze wykonać. `null` = węzeł ich nie wykonuje
   * (nie baza, bez prawa zapisu). `nullish`, bo starsza instancja tego pola nie odda.
   */
  doPrzerobienia: z.number().nullish(),
  wydaniaDolO: z.string().nullable().optional(),
  wydaniaDolBlad: z.string().nullable().optional(),
  wydaniaDolPytalO: z.string().nullable().optional(),
  /**
   * UWAGI Z OSTATNIEGO CYKLU — rzeczy warte wiedzenia, które NIE SĄ awarią.
   *
   * ⚠️ Osobno od `lastProblem`, bo inaczej się je czyta: „3315 wydań bez rozwiązanego pracownika"
   * jest prawdziwe przez tygodnie i w polu problemu świeciłoby na czerwono bez przerwy.
   */
  uwagi: z.array(z.string()).optional(),
  uwagiO: z.string().nullable().optional(),
  /**
   * TABELE W BAZIE TEGO WĘZŁA BEZ KLUCZA GŁÓWNEGO — z powodem i POLECENIAMI NAPRAWY.
   *
   * ⚠️⚠️ STRUKTURA, NIE PROZA — i to jest cała różnica względem `uwagi` wyżej. Portal wiedział
   * o tym od stycznia (agent melduje w każdym cyklu), ale wiedza siedziała w linijce tekstu
   * i nikt z niej nic nie zrobił: nigdzie nie stało, że skutkiem jest BRAK PLANOGRAMU wyspy.
   *
   * ⚠️ `polecenia` LICZY SERWER, bo wymagają kluczy naturalnych tabel SPS. Druga kopia tej listy
   * w kliencie rozjechałaby się przy pierwszej zmianie — i wtedy portal dyktowałby `ALTER TABLE`
   * z nieprawdziwym kluczem na produkcyjnej bazie klienta.
   *
   * `optional`, bo starsza instancja tego pola nie odda; wtedy zostają same uwagi.
   */
  bezKlucza: z
    .object({ tabele: z.array(z.string()), skutki: z.array(z.string()), polecenia: z.array(z.string()) })
    .nullable()
    .optional(),
  /**
   * CO AGENT ZASTAŁ NA MASZYNIE — fakty z `SPSConfig.XML`, nie deklaracje człowieka.
   * To na tej podstawie podejmuje się decyzję o potwierdzeniu, więc ekran musi je pokazać
   * wszystkie, a nie streszczać do jednej etykiety.
   */
  hostname: z.string().nullable(),
  nodeRole: z.string().nullable(),
  spsServer: z.string().nullable(),
  spsDatabase: z.string().nullable(),
  spsVmid: z.number().nullable(),
  /** Adres, KTÓRY MASZYNA PODAJE O SOBIE — to jest „IP automatu". Portal go nie zobaczy, bo NAT. */
  machineIp: z.string().nullable().optional(),
  /** Adres ŹRÓDŁOWY połączenia widziany przez instancję. Rozjazd z powyższym = NAT albo proxy. */
  lastSeenIp: z.string().nullable().optional(),
  /**
   * ZEGAR MASZYNY — rozjazd wobec portalu w sekundach, dodatni = idzie do przodu.
   *
   * ⚠️ `null` ≠ 0. Zero znaczy „zegar się zgadza"; `null` znaczy „agent tego nie przysłał"
   * (starsza wersja albo jeszcze się nie zmeldował). Ekran musi te dwa stany rozróżniać,
   * bo pokazanie „0 s" przy braku pomiaru jest twierdzeniem, którego nikt nie sprawdził.
   */
  clockSkewSeconds: z.number().nullable().optional(),
  /** Czas systemowy maszyny z ostatniego meldunku — NAPIS, bo to odczyt zegara, o którym wątpimy. */
  machineTime: z.string().nullable().optional(),
  /**
   * Typ startu usługi czasu Windows. To ONO odróżnia rozjazd, który zniknie sam, od trwałego:
   * przy `wylaczona` maszyna nie ma czym wyrównać zegara i żadne czekanie nie pomoże.
   */
  timeSyncStart: z.enum(['automatyczny','reczny','wylaczona','nieznany']).nullable().optional(),
  timeSyncRunning: z.boolean().nullable().optional(),
  /** Strefa Windows w zapisie IANA. Sens ma dopiero w zestawieniu z `group.timeZone`. */
  timeZone: z.string().nullable().optional(),
  utcOffsetMinutes: z.number().nullable().optional(),
  /**
   * KOPIE LEŻĄCE NA DYSKU MASZYNY — spis z ostatniego pomiaru agenta, najnowsze pierwsze.
   *
   * ⚠️ `null`/brak ≠ pusta tablica. Pusta znaczy „agent sprawdził i nic tam nie leży", brak —
   * „ta maszyna w ogóle nie prowadzi kopii" (podległa albo bez skonfigurowanego katalogu).
   * Ekran musi te dwa stany rozróżniać, bo drugi nie jest niczyim problemem.
   */
  localBackups: z
    .array(z.object({ nazwa: z.string(), rozmiar: z.number(), utworzono: z.string(), suma: z.string() }))
    .nullable()
    .optional(),
  /**
   * CO ŚLEDZI ZMIANY W BAZIE TEGO WĘZŁA — surowy pomiar agenta plus policzone podsumowanie.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️ DWA POLA, DWIE ROLE: `sledzenie` odpowiada „ile" (liczone w API, bo wymaga listy tabel,
   *  które portal zna), a `sledzenieZmian` „które" — po nazwie, do okienka z listą.
   *
   *  ⚠️ WSZYSTKO `optional`, bo instancja i agent starsze od tego pomiaru nic nie przyślą —
   *  a lista agentów to jedyny ekran, z którego potwierdza się węzły. Wymagane pole wywaliłoby
   *  walidację CAŁEJ listy i zablokowało wdrożenie na starszej flocie.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  /**
   * GRANICA ŻYWYCH WYDAŃ — `null` znaczy „nie ma zaległości".
   *
   * ⚠️ PO TYM EKRAN ODRÓŻNIA DWIE RÓŻNE RZECZY: „kanał stoi i nowe wydania nie jadą" od
   * „nowe wydania jadą, a historia dociąga się w tle". Do 02.09.2026 były nieodróżnialne,
   * bo kursor był jeden — i dlatego trzygodzinne dociąganie wyglądało jak awaria synchronizacji.
   */
  wydaniaZyweOd: z.number().nullable().optional(),
  sledzenie: z
    .object({
      cokolwiek: z.boolean(),
      tabelCt: z.number(),
      tabelCdc: z.number(),
      tabelNaszych: z.number(),
      triggeryCzynne: z.number(),
      triggeryWszystkie: z.number(),
      /** Triggery SPOZA mechanizmu SPS-a — dopiero to jest cudzy, nieznany ślad. */
      triggeryInne: z.number().optional(),
      /**
       * REPLIKACJA SUPPLYPOINTA. `null` = agent tej wersji jej nie mierzył.
       *
       * ⚠️ TO NIE JEST NATYWNY CHANGE TRACKING, choć nazwa („SyncChangeTracking") sugeruje to
       * samo. U klienta to bywa NAJWIĘKSZA TABELA W BAZIE — zmierzone 471 MB przy wyłączonym
       * CT i CDC, czyli dokładnie tam, gdzie panel pisał „Zajmuje 0.0 MB".
       */
      spsSync: z
        .object({
          jest: z.boolean(),
          triggeryCzynne: z.number(),
          triggeryWszystkie: z.number(),
          wierszy: z.number().nullable(),
          bajty: z.number().nullable(),
          naDobe: z.number().nullable(),
          najstarszy: z.string().nullable(),
          /** `true` = triggery piszą, a nikt tego nie odbiera. `null` = nie da się orzec. */
          wProznie: z.boolean().nullable(),
          programowObecnych: z.number().nullable(),
          programowWszystkich: z.number().nullable(),
          piszacy: z.array(z.object({ tabela: z.string(), wpisow: z.number() })),
          probka: z.number(),
          jestCoWylaczac: z.boolean(),
        })
        .nullable()
        .optional(),
      /** `null` = choć jednego z włączonych mechanizmów nie zmierzono. NIE to samo co zero. */
      bajtyRazem: z.number().nullable(),
      bledy: z.array(z.string()),
    })
    .nullable()
    .optional(),
  sledzenieZmian: z
    .object({
      ct: z.object({
        wlaczony: z.boolean(),
        retencja: z.number().nullable(),
        jednostka: z.string().nullable(),
        autoCleanup: z.boolean().nullable(),
        wersja: z.string().nullable(),
        bajty: z.number().nullable(),
      }),
      cdc: z.object({ wlaczony: z.boolean(), bajty: z.number().nullable() }),
      /** Surowa część mechanizmu SPS-a — okienko wypisuje z niej tabele i katalog programów. */
      spsSync: z
        .object({
          jest: z.boolean(),
          tabele: z.array(z.object({ nazwa: z.string(), wierszy: z.number().nullable(), bajty: z.number().nullable() })),
          relacji: z.number().nullable(),
          przetworzonych: z.number().nullable(),
          programy: z.array(z.object({ nazwa: z.string(), jest: z.boolean() })).nullable(),
          katalogProgramow: z.string().nullable(),
          najnowszy: z.string().nullable(),
        })
        .optional(),
      triggery: z.array(z.object({ nazwa: z.string(), tabela: z.string(), wylaczony: z.boolean() })),
      tabele: z.array(z.object({ nazwa: z.string(), ct: z.boolean(), cdc: z.boolean(), wierszy: z.number() })),
      tabelWSumie: z.number(),
      bledy: z.array(z.string()),
    })
    .nullable()
    .optional(),
  sledzenieZmianAt: z.string().nullable().optional(),
  /**
   * OSTATNIE WYŁĄCZENIE REPLIKACJI SPS-A na tym węźle.
   *
   * ⚠️⚠️ ISTNIEJE Z POWODU ZGŁOSZENIA (03.09.2026): „w logach mamy tylko, że poszło, ale na
   * stronie nie ma nic, że cokolwiek się udało". Audyt notował ZLECENIE, a wynik zadania nie
   * miał gdzie się pokazać — w tym wynik najważniejszy, czyli „SPSUI nie wstał".
   */
  wylaczenieSpsSync: z
    .object({
      id: z.string(),
      /** ⚠️ Rodzaj jedzie razem, bo jeden komponent rysuje wynik OBU zadań wyłączających. */
      kind: z.string(),
      status: z.enum(MACHINE_TASK_STATUS),
      progress: z.number().nullable(),
      note: z.string().nullable(),
      error: z.string().nullable(),
      output: z.unknown().nullable(),
      createdAt: z.string(),
      finishedAt: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * OSTATNIE WYŁĄCZENIE NATYWNEGO CHANGE TRACKINGU na tym węźle.
   *
   * ⚠️ OSOBNE POLE, NIE WSPÓLNE Z REPLIKACJĄ SPS-A: to dwa różne mechanizmy i dwa różne zadania,
   * a człowiek musi widzieć wynik każdego osobno. Zlanie ich w jedno pokazywałoby wynik tego,
   * które przypadkiem było później.
   */
  wylaczenieCt: z
    .object({
      id: z.string(),
      /** ⚠️ Rodzaj jedzie razem, bo jeden komponent rysuje wynik OBU zadań wyłączających. */
      kind: z.string(),
      status: z.enum(MACHINE_TASK_STATUS),
      progress: z.number().nullable(),
      note: z.string().nullable(),
      error: z.string().nullable(),
      output: z.unknown().nullable(),
      createdAt: z.string(),
      finishedAt: z.string().nullable(),
    })
    .nullable()
    .optional(),
  diskFreeBytes: z.number().nullable().optional(),
  diskTotalBytes: z.number().nullable().optional(),
  diskVolume: z.string().nullable().optional(),
  /**
   * Kiedy powstał pomiar dysku. ⚠️ BYWA WYRAŹNIE STARSZY NIŻ `lastSeenAt`, bo spis liczy się
   * co kilka cykli — i właśnie dlatego jedzie osobno, zamiast dziedziczyć czas sygnału życia.
   */
  diskAt: z.string().nullable().optional(),
  acceptance: z.enum(ACCEPTANCE),
  acceptedAt: z.string().nullable(),
  acceptedByEmail: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectedByEmail: z.string().nullable(),
  rejectReason: z.string().nullable(),
  group: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    /** Strefa OCZEKIWANA na tej wyspie — przypięta przez pierwszego agenta. */
    timeZone: z.string().nullable().optional(),
    /**
     * Baza MASTER wyspy. Sens ma dopiero w zestawieniu z `spsDatabase` agenta: różnica znaczy,
     * że maszyna pracuje na innej bazie, niż portal pokazuje.
     */
    masterDatabase: z.string().nullable().optional(),
    /**
     * KTÓRY WĘZEŁ HOSTUJE MASTERA — z samej nazwy bazy to NIE WYNIKA. W trybie `SMART_SYNC`
     * wszystkie węzły wyspy mają bazę o tej samej nazwie, więc bez tego pola nie da się pokazać,
     * który z nich jest masterem. `optional`, bo starsza instancja tego pola nie odda.
     */
    masterAgentId: z.string().nullable().optional(),
    /** Zgoda na korektę zegara maszyn tej wyspy — decyduje portal, nie plik na maszynie. */
    zegarNaprawiaj: z.boolean().optional(),
    /**
     * TRYB I WŁĄCZNIK ROZSYŁANIA — bez nich kursor rodzeństwa jest nieczytelny. „Kursor pusty"
     * jest NORMĄ na wyspie z wyłączonym rozsyłaniem i AWARIĄ na wyspie z włączonym.
     */
    tryb: z.string().optional(),
    wydaniaRodzenstwa: z.boolean().optional(),
    client: z.object({ name: z.string() }),
  }),
  machine: z.object({ id: z.string(), vmid: z.number(), name: z.string() }).nullable(),
  /**
   * KOD, KTÓRYM WĘZEŁ WSZEDŁ. Pokazywany razem z agentem, bo odrzucenie blokuje JEDEN węzeł,
   * a kod nadal wpuszcza kolejne — człowiek patrzący na podejrzane zgłoszenie musi widzieć,
   * czym ono weszło, w tej samej chwili.
   */
  enrollment: z
    .object({
      id: z.string(),
      label: z.string(),
      prefix: z.string(),
      revokedAt: z.string().nullable(),
      expiresAt: z.string().nullable(),
    })
    .nullable(),
});

export type AgentRow = z.infer<typeof AgentRowSchema>;
export type Acceptance = AgentRow['acceptance'];

const ListSchema = z.object({
  agents: z.array(AgentRowSchema),
  /** Liczba oczekujących — WYLICZANA z tej samej listy, nie trzymana nigdzie jako stan. */
  pendingCount: z.number(),
});

const ActionSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  acceptance: z.enum(ACCEPTANCE),
  /** `false` = stan już był taki; przycisk kliknięto dwa razy albo ktoś to zrobił wcześniej. */
  changed: z.boolean(),
});

export const listAgents = (groupId?: string) =>
  apiGet(`/agents${groupId ? `?groupId=${encodeURIComponent(groupId)}` : ''}`, ListSchema);

/**
 * ODWOŁANE TOKENY, KTÓRE WCIĄŻ PRÓBUJĄ — próby z ostatnich 30 dni (Gestamp Września, 08.10.2026).
 *
 * ⚠️ ŁAGODNIE (`passthrough`, `powod` jako napis): to lista pomocnicza pod główną listą agentów — schemat
 * ostrzejszy od odpowiedzi wywróciłby tę sekcję przy pierwszym nowym polu albo powodzie po stronie API.
 */
const OdwolanyTokenSchema = z
  .object({
    id: z.string(),
    nodeId: z.string(),
    kind: z.string(),
    label: z.string(),
    prefix: z.string(),
    wyspa: z.object({ id: z.string(), code: z.string(), name: z.string() }),
    powod: z.string(),
    revokedAt: z.string().nullish(),
    expiresAt: z.string().nullish(),
    firstDeniedAt: z.string().nullish(),
    lastDeniedAt: z.string().nullish(),
    lastDeniedIp: z.string().nullish(),
    /** Węzeł o tej nazwie ma działający token — puka INNA maszyna (np. stary komputer po wymianie). */
    wezelMaKlucz: z.boolean().default(false),
  })
  .passthrough();
export type OdwolanyToken = z.infer<typeof OdwolanyTokenSchema>;

export const fetchOdwolaneProbujace = () =>
  apiGet('/agents/odwolane', z.object({ rows: z.array(OdwolanyTokenSchema) }));

export const acceptAgent = (id: string) => apiPost(`/agents/${id}/accept`, {}, ActionSchema);

export const rejectAgent = (id: string, reason?: string) =>
  apiPost(`/agents/${id}/reject`, reason ? { reason } : {}, ActionSchema);

/**
 * USUNIĘCIE WĘZŁA — sprzątanie po nieudanej rejestracji, nie „mocniejsze odrzucenie".
 *
 * ⚠️ Instancja odwołuje przy tym token węzła. Bez tego wiersz WRACAŁBY przy najbliższym sygnale
 * życia (agent robi `upsert`), czyli po minucie — i wyglądałby jak nowe zgłoszenie.
 */
export const usunAgenta = (id: string) =>
  apiSend(
    'DELETE',
    `/agents/${id}`,
    undefined,
    z.object({ id: z.string(), nodeId: z.string(), usuniety: z.boolean(), odwolaneTokeny: z.number() }),
  );

/**
 * ODINSTALOWANIE AGENTA Z MASZYNY + usunięcie węzła (13.10.2026) — także potwierdzonego i offline.
 * Polecenie dochodzi do maszyny, gdy ta zapuka do portalu. Potwierdzenie: przepisana nazwa węzła.
 */
export const odinstalujAgenta = (id: string, potwierdzenie: string) =>
  apiPost(
    `/agents/${id}/odinstaluj`,
    { potwierdzenie },
    z.object({ id: z.string(), nodeId: z.string(), zleconeTokeny: z.number(), odwolaneTokeny: z.number() }),
  );

/** Odinstalowanie maszyny pukającej ODWOŁANYM tokenem — tylko ten token, nie jego węzeł. */
export const odinstalujOdwolany = (tokenId: string) =>
  apiPost(`/agents/odwolane/${tokenId}/odinstaluj`, {}, z.object({ tokenId: z.string(), nodeId: z.string(), zlecono: z.boolean() }));

/** Zlecone odinstalowanie — łagodnie (`passthrough`), z tego samego powodu co lista odwołanych. */
const OdinstalowanieSchema = z
  .object({
    id: z.string(),
    wyspa: z.string(),
    nodeId: z.string(),
    hostname: z.string().nullish(),
    powod: z.string(),
    zlecilEmail: z.string(),
    zlecono: z.string(),
    pierwszaProbaO: z.string().nullish(),
    ostatniaProbaO: z.string().nullish(),
    ostatnieIp: z.string().nullish(),
    przyjetoO: z.string().nullish(),
    meldunek: z.string().nullish(),
  })
  .passthrough();
export type Odinstalowanie = z.infer<typeof OdinstalowanieSchema>;

export const fetchOdinstalowania = () => apiGet('/agents/odinstalowania', z.object({ rows: z.array(OdinstalowanieSchema) }));

// ─────────────────────────────────────────────────────────────────────────────
//  KODY REJESTRACYJNE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ Kod wraca RAZ i nigdzie nie jest przechowywany w postaci jawnej — w bazie leży SHA-256.
 * Nie ma i nie może być trasy „pokaż kod": gdyby była, serwer musiałby trzymać plaintext.
 */
const WydanySchema = z.object({
  code: z.string(),
  id: z.string(),
  prefix: z.string(),
  maxUses: z.number(),
  expiresAt: z.string(),
  founding: z.boolean(),
  /** Tryb, w jakim wyspa się URODZI. `null` przy kodzie dołączającym — trybu istniejącej nie dotyka. */
  tryb: z.enum(['ZWYKLY', 'SMART_SYNC']).nullable(),
  island: z.object({ code: z.string(), name: z.string(), clientName: z.string() }),
  masterDatabase: z.string().nullable(),
  note: z.string(),
});
export type WydanyKod = z.infer<typeof WydanySchema>;

const UzycieSchema = z.object({
  at: z.string(),
  remoteAddr: z.string().nullable(),
  hostname: z.string().nullable(),
  nodeRole: z.string().nullable(),
  spsVmid: z.number().nullable(),
  /** ODMOWA jest tu wpisem najciekawszym — znaczy, że ktoś ma plik i użył go poza wdrożeniem. */
  accepted: z.boolean(),
  detail: z.string().nullable(),
});

const KodSchema = z.object({
  id: z.string(),
  label: z.string(),
  prefix: z.string(),
  founding: z.boolean(),
  groupId: z.string().nullable(),
  /**
   * Co ten kod ZAŁOŻY. Bez tego dwa kody założycielskie wyglądają na liście identycznie.
   *
   * ⚠️ TO NIE JEST TRYB WYSPY. Przy kodzie DOŁĄCZAJĄCYM zostaje tu domyślne `ZWYKLY`, bo
   * dołączenie trybu nie dotyka — na ekran idzie więc `group.tryb`, patrz `trybNaLiscieKodow`.
   */
  tryb: z.enum(['ZWYKLY', 'SMART_SYNC']),
  /** Tryb WYSPY, do której kod należy; `null`, gdy wyspy jeszcze nie ma (nieużyty założycielski). */
  group: z.object({ tryb: z.enum(['ZWYKLY', 'SMART_SYNC']) }).nullable(),
  islandName: z.string().nullable(),
  islandCode: z.string().nullable(),
  maxUses: z.number(),
  useCount: z.number(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
  createdByEmail: z.string(),
  uses: z.array(UzycieSchema),
});
export type KodRejestracyjny = z.infer<typeof KodSchema>;

const ListaKodowSchema = z.object({ enrollments: z.array(KodSchema) });

export const listEnrollments = (clientId: string) =>
  apiGet(`/agent-enrollments?clientId=${encodeURIComponent(clientId)}`, ListaKodowSchema);

/** Kod DOŁĄCZAJĄCY do istniejącej wyspy. */
export const issueJoinCode = (body: { groupId: string; label?: string; maxUses: number; expiresInHours: number }) =>
  apiPost('/agent-enrollments', body, WydanySchema);

/**
 * Kod ZAŁOŻYCIELSKI — wyspa powstanie z pierwszej rejestracji.
 *
 * Podajemy to, co wie CZŁOWIEK (klient, nazwa i kod obiektu); serwer i nazwę bazy przyniesie
 * pierwszy agent, bo tego człowiek nie wie.
 */
export const issueFoundingCode = (body: {
  clientId: string;
  islandName: string;
  /**
   * ⚠️ OBA NIEOBOWIĄZKOWE, BO WYLICZA JE INSTANCJA. Kod wyspy powstaje z nazwy obiektu i to
   * instancja rozstrzyga jego kolizję (dokłada numer), a opis kodu — też z nazwy. Wymaganie ich
   * tutaj znaczyło, że ekran musiał kazać człowiekowi wpisać dwie wartości techniczne, żeby dostać
   * jedną potrzebną. Wysyłamy je wyłącznie wtedy, gdy ktoś świadomie wpisał swoje.
   */
  islandCode?: string;
  label?: string;
  maxUses: number;
  expiresInHours: number;
  /**
   * TRYB, W JAKIM WYSPA MA SIĘ URODZIĆ.
   *
   * ⚠️ NIE DA SIĘ TEGO POPRAWIĆ PÓŹNIEJ BEZ PONOWNEJ INSTALACJI. Prawo maszyny do synchronizacji
   * rozstrzyga się w chwili JEJ rejestracji i nie jest przeliczane, a przy odmowie agent nie
   * dostaje nawet dostępu do bazy. Wyspa przełączona na Smart Sync po instalacji maszyn zostawia
   * je bez jednego i bez drugiego (zmierzone na Lockerboksie testówki, 24.08.2026).
   */
  tryb?: 'ZWYKLY' | 'SMART_SYNC';
}) => apiPost('/agent-enrollments', body, WydanySchema);

export const revokeEnrollment = (id: string) =>
  apiPost(`/agent-enrollments/${id}/revoke`, {}, z.object({ id: z.string(), revoked: z.boolean(), alreadyRevoked: z.boolean() }));

// ─────────────────────────────────────────────────────────────────────────────
//  INSTALATOR AGENTA — kompletne archiwum z obrazu instancji
//
//  ⚠️ Do 2026-08-18 ten ekran wydawał paczkę AKTUALIZACJI (`agent-*.json`, 283 KB, same
//  źródła), której na czystej maszynie nie da się użyć. Teraz jest to archiwum ZIP z Node'em,
//  zależnościami, nssm-em i kreatorem instalacji.
// ─────────────────────────────────────────────────────────────────────────────

const InstalatorSchema = z.object({
  /**
   * ⚠️ ZAWSZE `true`, ZOSTAJE DLA ZGODNOŚCI. Paczka agenta leży w obrazie instancji
   * (`ODPOWIEDZI.md`, E1), więc nie ma czego konfigurować i nie ma stanu „brak ustawień".
   * Pole zostaje, bo ekran je czyta, a instancja i front wdrażane są parami, lecz nie zawsze
   * w tej samej sekundzie.
   */
  configured: z.boolean(),
  ready: z.boolean(),
  /**
   * ⚠️ `nullable`, NIE TYLKO `optional`. Instancja odsyła `note: null`, gdy nie ma o czym mówić —
   * schemat bez `nullable` odrzucałby CAŁĄ odpowiedź i ekran pokazywałby błąd walidacji zamiast
   * przycisku pobierania.
   */
  note: z.string().nullable().optional(),
  /**
   * DWA NUMERY, KTÓRE ŁATWO POMYLIĆ — i pomyłka wyszła na wdrożeniu.
   *
   * `packageVersion` to wersja agenta W ARCHIWUM instalatora (dostaje ją czysta maszyna przy
   * pierwszej instalacji), a `channelVersion` to wersja, którą ta instancja WYDAJE kanałem
   * aktualizacji — czyli ta, do której podciągają się już zainstalowane agenty. Numer w archiwum
   * bywa NIŻSZY i nie jest to usterka: instalatora nie przebudowujemy przy każdym wydaniu.
   *
   * ⚠️ PORÓWNANIA „czy agent jest w tyle" ROBI SIĘ WYŁĄCZNIE Z `channelVersion`. Wcześniej front
   * porównywał z numerem paczki instalatora, więc przy agencie na 0.6.96 malował strzałkę
   * „powinien się podnieść", a przy agencie na 0.5.97 nie malował jej wcale (zgłoszone 2026-08-18).
   */
  packageVersion: z.string().nullable().optional(),
  channelVersion: z.string().nullable().optional(),
  /** ⚠️ ZOSTAJE dla frontu starszego niż to API: nosiło wersję z archiwum, nie z kanału. */
  targetVersion: z.string().nullable().optional(),
  fileName: z.string().nullable().optional(),
  sizeBytes: z.number().nullable().optional(),
  /**
   * WARIANT BETA — jeden plik `.exe` z kreatorem GUI, wydawany OBOK `.zip`.
   *
   * ⚠️ `nullable` I `optional` NARAZ, bo brak tego pliku nie jest awarią: instancja z wcześniejszego
   * obrazu niesie tylko `.zip`, a ekran ma wtedy pokazać jedną drogę, nie błąd.
   */
  exeFileName: z.string().nullable().optional(),
  exeSizeBytes: z.number().nullable().optional(),
  /** Wersja Node'a wniesiona w archiwum i dzień jego złożenia — z `instalator.json` w paczce. */
  wersjaNode: z.string().nullable().optional(),
  zbudowano: z.string().nullable().optional(),
  builtAt: z.string().nullable().optional(),
  /**
   * ⚠️ POLA Z CZASÓW PANELU — `downloads`, `building`, `builtAt`, `lastError`. Panel SKŁADAŁ
   * paczkę na żądanie, więc miał co liczyć i co meldować. Teraz plik leży w obrazie: nic się nie
   * składa i nic nie może się nie udać. Zostawione jako opcjonalne, żeby starszy front nie padał
   * na nowym API — nowy ich nie pokazuje.
   */
  downloads: z.number().optional(),
  building: z.boolean().optional(),
  lastError: z.string().nullable().optional(),
  instanceLabel: z.string().optional(),
});
export type StanInstalatora = z.infer<typeof InstalatorSchema>;

export const installerInfo = () => apiGet('/agent-installer', InstalatorSchema);
export const installerPrepare = () => apiPost('/agent-installer/prepare', {}, InstalatorSchema);
/** Pobranie idzie przez nawigację przeglądarki — ciasteczko sesji jedzie samo. */
export const INSTALLER_DOWNLOAD_PATH = '/agent-installer/download';
/** Wariant BETA (kreator z GUI). Osobna ścieżka, żeby „ten link daje betę" dało się powiedzieć jednym zdaniem. */
export const INSTALLER_DOWNLOAD_EXE_PATH = '/agent-installer/download-exe';

/**
 * ZADANIA MASZYNOWE — kopia bazy zlecana węzłowi.
 *
 * ⚠️ ŻĄDANIE NIE NIESIE NAZWY BAZY i to nie jest przeoczenie. Węzeł kopiuje bazę, do której ma
 * dostęp — tę z własnej konfiguracji. Gdyby nazwa szła stąd, przeglądarka mogłaby wskazać dowolną
 * bazę na serwerze SQL klienta.
 */
/**
 * „AKTUALIZUJ TERAZ" — prośba o sprawdzenie wersji przy najbliższym sygnale życia.
 *
 * ⚠️ NIE JEST TO ZDALNE URUCHOMIENIE AKTUALIZACJI i ekran nie ma tego obiecywać. Portal nie puka
 * do agenta — zapisuje prośbę, a agent słyszy ją w odpowiedzi na swój najbliższy sygnał życia.
 * Skutek pojawia się w ciągu jednego cyklu (30–600 s).
 */
export const poprosOAktualizacje = (agentId: string) =>
  apiPost(
    `/agents/${agentId}/aktualizuj`,
    {},
    z.object({
      id: z.string(),
      nodeId: z.string(),
      poproszonoO: z.string(),
      wersjaPrzed: z.string().nullable(),
      waznaMinut: z.number(),
    }),
  );

export const zlecKopieBazy = (agentId: string, groupId: string) =>
  apiPost(`/agents/${agentId}/kopia-bazy`, { groupId }, z.object({ id: z.string() }));

/**
 * Kopia zostająca NA DYSKU MASZYNY — bez wysyłki do magazynu.
 *
 * ⚠️ Odmowa z braku miejsca przychodzi jako NIEUDANE ZADANIE, nie jako błąd tego wywołania:
 * wolne miejsce zna wyłącznie maszyna, więc decyzja zapada tam, a tu wraca samo „przyjęto".
 */
export const zlecKopieLokalna = (agentId: string, groupId: string) =>
  apiPost(`/agents/${agentId}/kopia-lokalna`, { groupId }, z.object({ id: z.string() }));

/** Wysłanie do magazynu kopii, która JUŻ leży na dysku maszyny — bez robienia nowej. */
export const zlecWyslanieKopii = (agentId: string, groupId: string, nazwa: string) =>
  apiPost(`/agents/${agentId}/kopia-wyslij`, { groupId, nazwa }, z.object({ id: z.string() }));

/** Ściągnięcie kopii z magazynu na dysk maszyny — wskazujemy ZADANIEM, nie kluczem obiektu. */
export const zlecPobranieKopii = (agentId: string, groupId: string, zadanieId: string) =>
  apiPost(`/agents/${agentId}/kopia-pobierz`, { groupId, zadanieId }, z.object({ id: z.string() }));

/** Skasowanie kopii z dysku maszyny — jedyny sposób zwolnienia miejsca bez wchodzenia na maszynę. */
export const zlecUsuniecieKopiiLokalnej = (agentId: string, groupId: string, nazwa: string) =>
  apiPost(`/agents/${agentId}/kopia-usun`, { groupId, nazwa }, z.object({ id: z.string() }));

/**
 * ODTWORZENIE BAZY z kopii leżącej na dysku maszyny.
 *
 * ⚠️ WYŁĄCZNIE NA NAZWĘ, KTÓREJ NA SERWERZE NIE MA. Odmowa przychodzi jako NIEUDANE ZADANIE, nie
 * jako błąd tego wywołania: czy baza istnieje, wie tylko serwer SQL na maszynie klienta, więc
 * pytanie zadaje się w chwili odtwarzania, a nie w chwili klikania.
 */
export const zlecOdtworzenie = (agentId: string, groupId: string, nazwa: string, baza: string) =>
  apiPost(`/agents/${agentId}/kopia-odtworz`, { groupId, nazwa, baza }, z.object({ id: z.string() }));

const WgranaSchema = z.object({ id: z.string(), nazwa: z.string(), rozmiar: z.number(), suma: z.string() });

/**
 * WGRANIE PACZKI Z KOMPUTERA OPERATORA prosto do magazynu — z pominięciem maszyny.
 *
 * ⚠️ `XMLHttpRequest`, A NIE `fetch`, I TO JEST CAŁY POWÓD ISTNIENIA TEJ FUNKCJI: `fetch` nie
 * raportuje postępu WYSYŁANIA. Przy stumegabajtowej paczce na łączu klienta znaczyłoby to kilka
 * minut ekranu, na którym nic się nie dzieje — a to jest dokładnie ta sytuacja, w której człowiek
 * klika drugi raz albo zamyka kartę. Dokładnie na to skarżył się użytkownik przy wysyłce kopii
 * („wisi na 75%"), tylko tam ślepy odcinek był po stronie agenta.
 *
 * ⚠️ CIAŁEM JEST SAM PLIK, bez `FormData`. Nagłówek `Content-Type` musi być jednym z dwóch, które
 * zna parser surowego ciała w API (`main.ts`) — inaczej Fastify odrzuca żądanie `415` jeszcze
 * zanim uchwyt cokolwiek zobaczy, i to po przesłaniu całej paczki.
 */
export function wgrajKopie(
  agentId: string,
  groupId: string,
  plik: File,
  postep?: (procent: number) => void,
): Promise<z.infer<typeof WgranaSchema>> {
  return new Promise((rozwiaz, odrzuc) => {
    const xhr = new XMLHttpRequest();
    const adres =
      `${API_BASE}/agents/${agentId}/kopia-wgraj` +
      `?groupId=${encodeURIComponent(groupId)}&nazwa=${encodeURIComponent(plik.name)}`;

    xhr.open('PUT', adres);
    xhr.withCredentials = true;
    xhr.setRequestHeader(
      'Content-Type',
      /\.zip$/i.test(plik.name) ? 'application/zip' : 'application/octet-stream',
    );

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && postep !== undefined) postep(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      const tresc: unknown = ((): unknown => {
        try {
          return JSON.parse(xhr.responseText) as unknown;
        } catch {
          return null;
        }
      })();
      if (xhr.status >= 200 && xhr.status < 300) {
        const wynik = WgranaSchema.safeParse(tresc);
        wynik.success ? rozwiaz(wynik.data) : odrzuc(new Error('Instancja przyjęła paczkę, ale odpowiedź jest nie ta'));
        return;
      }
      /** Komunikat z API mówi, CO poszło nie tak; sam kod stanu nie mówi nic używalnego. */
      const wiadomosc =
        tresc !== null && typeof tresc === 'object' && 'message' in tresc
          ? String((tresc as { message: unknown }).message)
          : `Wysyłka odrzucona (HTTP ${xhr.status})`;
      odrzuc(new Error(wiadomosc));
    };
    /** ⚠️ Sieć urwana w połowie nie daje ŻADNEJ treści — bez tego zdania zostałby pusty błąd. */
    xhr.onerror = () => odrzuc(new Error('Połączenie przerwane w trakcie wysyłki — paczka nie doszła w całości'));
    xhr.onabort = () => odrzuc(new Error('Wysyłka przerwana'));

    xhr.send(plik);
  });
}

/**
 * Nazwa bazy podpowiadana przy odtwarzaniu — z nazwy paczki.
 *
 * ⚠️ TO TYLKO PODPOWIEDŹ DLA CZŁOWIEKA. Rozstrzyga wpisane pole, a nazwę i tak sprawdza agent
 * wobec własnego serwera. Odpowiednik `nazwaBazyZPaczki` po stronie agenta — świadomie osobny,
 * bo tamten pracuje na pełnych ścieżkach systemu plików maszyny.
 */
export const bazaZNazwyPaczki = (plik: string): string =>
  plik.replace(/\.bak(\.zip)?$/i, '').replace(/_\d{14}$/, '');

/**
 * Przerwanie zadania, które utknęło.
 *
 * ⚠️ Znaczy „przestań na to czekać", nie „zatrzymaj agenta": agenta nie da się przerwać w pół
 * wysyłki, ale jego spóźniony wynik zostanie odrzucony po statusie.
 */
export const przerwijZadanie = (zadanieId: string, groupId: string) =>
  apiPost(`/agents/zadania/${zadanieId}/przerwij`, { groupId }, z.object({ przerwano: z.boolean() }));


const MachineTaskSchema = z.object({
  id: z.string(),
  /**
   * ⚠️ KOMPLET RODZAJÓW Z `MachineTaskKind`, nie te, które akurat wyświetlamy. Brakowało tu
   * `OBRAZY_ZGRAJ` i skutek był zdradliwy: `/machine-tasks` odpowiadało 200, a Zod wywracał
   * CAŁĄ listę na jednym nieznanym wierszu — ekran zostawał na „Ładowanie…" bez błędu w konsoli,
   * bo zapytanie krążyło w ponowieniach. Zmierzone na DBW, gdzie takie zadanie istnieje;
   * na warsztacie go nie było, więc lokalnie wszystko wyglądało dobrze.
   */
  /**
   * ⚠️ KOMPLET RODZAJÓW Z BAZY, także tych, których ten ekran NIE POKAZUJE. Zod odrzuca cały
   * wiersz o nieznanym `kind`, a react-query ponawia zapytanie — ekran zostaje wtedy na
   * „Ładowanie…" bez błędu w konsoli. `rodzaje-zadan.spec.ts` pilnuje tej listy wobec Prismy.
   */
  kind: z.enum([
    'OBRAZ_WGRAJ',
    /**
     * PODGLĄD jednego pliku obrazka z maszyny — czysty odczyt z terminem ważności (`OBRAZ_POKAZ`).
     *
     * ⚠️ To NIE zgrywanie biblioteki: plik nie zostaje u nas i nie staje się wzorcem treści.
     */
    'OBRAZ_POKAZ',
    /** SPIS grafik na maszynie — sam odczyt katalogu, bez przenoszenia bajtów (`OBRAZY_SPIS`). */
    'OBRAZY_SPIS',
    'OBRAZY_ZGRAJ',
    'KOPIA_BAZY',
    'KOPIA_LOKALNA',
    'KOPIA_WYSLIJ',
    'KOPIA_POBIERZ',
    'KOPIA_USUN',
    'KOPIA_ODTWORZ',
    'KOPIA_WGRANA',
    /** Instalacja programu na maszynie — patrz ekran „Programy na maszynach". */
    'PROGRAM_ZAINSTALUJ',
    /**
     * Podgląd CAŁEGO `SPSConfig.XML` — tylko odczyt, nie tyka pliku ani procesu SPSUI.
     *
     * ⚠️ NIE MYLIĆ Z `SPSCONFIG_ZAPISZ`, choć nazwa jest bliska: tamten CELOWO restartuje SPSUI,
     * ten nie dotyka niczego. Podgląd wolno zlecić na pracującej maszynie w środku dnia.
     */
    'SPSCONFIG_POKAZ',
    /**
     * Zapis `SPSConfig.XML`: baza, serwer albo numer automatu.
     *
     * ⚠️ JEDNO Z DWÓCH ZADAŃ, KTÓRE RESTARTUJĄ SPSUI — drugim jest `SPS_SYNC_WYLACZ` niżej.
     */
    'SPSCONFIG_ZAPISZ',
    /**
     * Wyłączenie replikacji SupplyPointa („SyncChangeTracking") na maszynie.
     *
     * ⚠️ TO ZATRZYMUJE AUTOMAT: agent wyłącza SPSUI, uruchamia `spsdbupdate.exe
     * -removereplication`, czyści tabele `Sync*` partiami, usuwa programy kanału i podnosi
     * interfejs. Procedura i zmierzone liczby: `agent/src/zadania/sps-sync-wylacz.ts`.
     */
    'SPS_SYNC_WYLACZ',
    /**
     * Wyłączenie natywnego Change Trackingu MSSQL-a.
     *
     * ⚠️ INNY MECHANIZM NIŻ `SPS_SYNC_WYLACZ` i — w odróżnieniu od niego — NIE zatrzymuje
     * automatu: to zmiana metadanych per tabela. Szczegóły: `agent/src/zadania/ct-wylacz.ts`.
     */
    'CT_WYLACZ',
    'SPSUI_URUCHOM',
    /**
     * SKRÓT URUCHOMIENIOWY Z OKNA PULPITU — SPS Utilities albo katalog plików przysłanych agentem.
     *
     * ⚠️ `input.co` to nazwa ZE ZBIORU, nigdy ścieżka: portal nie mówi agentowi, CO wykonać,
     * tylko KTÓRY ze znanych mu skrótów uruchomić.
     */
    'SKROT_URUCHOM',
    /**
     * WGRANIE PLIKU NA MASZYNĘ — plik z portalu ląduje w `C:\sdi\przeslane`, nic go nie uruchamia.
     *
     * ⚠️ OSOBNY RODZAJ OD `PROGRAM_ZAINSTALUJ`: tamten URUCHAMIA to, co przyszło, więc wymaga
     * podpisu i kodów wyjścia. „Wrzuć mi plik" i „uruchom to z prawami administratora" to dwie
     * zupełnie różne zgody.
     */
    'PLIK_WGRAJ',
    /**
     * RESTART ALBO WYLACZENIE MASZYNY — jedyny rodzaj, po ktorym automat NA PEWNO przestaje wydawac.
     *
     * ⚠️ `WYLACZ` jest nieodwracalny zdalnie: maszyna nie wroci, dopoki ktos fizycznie jej nie
     * wlaczy. Dlatego ekran pyta dwa razy, a serwer wymaga potwierdzenia w ciele zadania.
     */
    'ZASILANIE',
    /**
     * ODCZYT WIERSZY JEDNEJ TABELI DO PORÓWNANIA — świeży `SELECT` na każdym węźle z bazą.
     *
     * ⚠️ Czysty odczyt: to zadanie odpowiada na „którego wiersza brakuje", a nie na „ile ich jest".
     */
    'ROZJAZD_ODCZYT',
    /**
     * ILE STOI W AUTOMATACH WEDŁUG BAZY TEGO WĘZŁA — sumy `QTYCURRENT`/`QTYMAX` na automat.
     *
     * ⚠️ Osobny rodzaj od `ROZJAZD_ODCZYT`, bo tamten świadomie WYCINA stan magazynowy: między
     * klonami różni się z definicji i w porównaniu wierszy dawałby różnicę w każdym z nich.
     * Tutaj ta różnica jest pytaniem.
     */
    'ZAJETOSC_ODCZYT',
    /**
     * ILE WIERSZY MA KAŻDA ŚLEDZONA TABELA W BAZIE TEGO WĘZŁA — same liczby, bez treści.
     *
     * ⚠️ Osobny rodzaj od `ROZJAZD_ODCZYT`: tamten oddaje TREŚĆ wierszy jednej tabeli, żeby
     * powiedzieć, którego brakuje. Ten odpowiada na „ile ich jest" dla wszystkich naraz.
     */
    'STAN_TABEL',
    /** Zapytanie SQL z „Działań na bazie" — historia zadań go nie pokazuje, ale schemat musi go znać. */
    'ZAPYTANIE_SQL',
    'ZRZUT_EKRANU',
    /**
     * PULPIT NA ŻYWO — jedyny rodzaj, który TRWA tyle, ile patrzy człowiek.
     *
     * ⚠️ Na liście zadań wygląda jak długo biegnące zadanie i tak ma być: jego wynikiem jest
     * zestawienie ruchu po zamknięciu sesji, a nie plik ani liczba wierszy.
     */
    'PULPIT_OTWORZ',
  ]),
  status: z.enum(MACHINE_TASK_STATUS),
  progress: z.number().nullable(),
  note: z.string().nullable(),
  error: z.string().nullable(),
  output: z.unknown().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  agent: z.object({
    id: z.string(),
    nodeId: z.string(),
    /** Kiedy automat ostatnio się meldował — odróżnia „czeka w kolejce" od „milczy". Starsza instancja go nie odda. */
    lastSeenAt: z.string().nullable().optional(),
    machine: z.object({ id: z.string(), vmid: z.number(), name: z.string() }).nullable(),
  }),
});

export type MachineTask = z.infer<typeof MachineTaskSchema>;

/**
 * Zadania maszynowe wyspy — opcjonalnie ZAWĘŻONE DO JEDNEJ PACZKI.
 *
 * ⚠️ `batchId` NIE JEST OZDOBNIKIEM. Okno wgrywania obrazka pokazuje postęp „wgrano na N z M
 * automatów"; bez tego zawężenia liczyło WSZYSTKIE zadania tego rodzaju na wyspie, czyli także
 * z poprzednich wgrań. Zgłoszone z DBW: „Wgrano na 7 z 10 automatów", a na tej wyspie stoją
 * DWA automaty. Gorsze od samej liczby było to, że pod spodem wyświetlały się błędy ze STARYCH
 * paczek jako błędy trwającego wgrania.
 */
export const fetchMachineTasks = (groupId: string, batchId?: string) =>
  apiGet(
    `/machine-tasks?groupId=${groupId}${batchId === undefined ? '' : `&batchId=${batchId}`}`,
    z.array(MachineTaskSchema),
  );

/**
 * Bilet na pobranie gotowej kopii.
 *
 * ⚠️ DOSTAJEMY LINK, NIE PLIK. Kopia to dziesiątki megabajtów; przeciąganie ich przez API
 * zajmowałoby połączenia potrzebne reszcie portalu. Link jest podpisany i ograniczony czasem,
 * więc pobieramy prosto z magazynu.
 */
/**
 * Usunięcie PLIKU kopii z magazynu — wpis zadania zostaje.
 *
 * ⚠️ NIEODWRACALNE i bez kosza: kopia bazy klienta nie ma po naszej stronie drugiego egzemplarza.
 * Ekran musi zapytać PRZED wywołaniem, a nie pokazać „cofnij" po fakcie.
 */
export const usunKopie = (id: string, groupId: string) =>
  apiSend('DELETE', `/machine-tasks/${id}/plik?groupId=${groupId}`, undefined, z.object({ usunieto: z.boolean() }));

/**
 * Uprzątnięcie NIEUDANYCH wpisów z ekranu kopii.
 *
 * ⚠️ KASUJE WIERSZE, nie pliki — nieudane zadanie żadnego nie zostawiło. To odwrotność `usunKopie`,
 * które zdejmuje plik i ZOSTAWIA wiersz, bo tamten mówi, kto i kiedy zrobił kopię. Tu nie ma czego
 * mówić, a ślad zlecenia zostaje w dzienniku audytu.
 */
export const wyczyscNieudaneKopie = (groupId: string) =>
  apiSend('DELETE', `/machine-tasks/nieudane?groupId=${groupId}`, undefined, z.object({ usunieto: z.number() }));

/**
 * SPRZĄTNIĘCIE WPISÓW PO KOPIACH, KTÓRYCH PLIKU JUŻ NIE MA — na żądanie.
 *
 * ⚠️ KASUJE WIERSZE, nie pliki: plików tu nie ma, to jest definicja tego zbioru. Ekran ukrywa
 * takie wiersze sam po tygodniu (`lib/kopie-widok.ts`), a to jest droga dla kogoś, kto chce mieć
 * czysto od razu.
 */
export const wyczyscKopieBezPliku = (groupId: string) =>
  apiSend('DELETE', `/machine-tasks/bez-pliku?groupId=${groupId}`, undefined, z.object({ usunieto: z.number() }));

/**
 * ⚠️ USUNIĘTE `fetchTaskLink`. Trasa `/machine-tasks/:id/link` oddawała podpisany adres MinIO,
 * a magazyn przestał być publiczny (`ODPOWIEDZI.md`, A) — plik idzie teraz strumieniem przez
 * `/api/machine-tasks/:id/plik`, na który wystarczy zwykły odnośnik z ciasteczkiem sesji.
 */

/** Podsumowanie śledzenia zmian — liczone w API (`ocena-sledzenia.ts`). */
export type PodsumowanieSledzenia = NonNullable<z.infer<typeof AgentRowSchema>['sledzenie']>;
/** Surowy pomiar agenta — po nazwach tabel rysuje się okienko „gdzie to chodzi". */
export type SledzenieSurowe = NonNullable<z.infer<typeof AgentRowSchema>['sledzenieZmian']>;

/**
 * WYŁĄCZENIE REPLIKACJI SUPPLYPOINTA NA WĘŹLE.
 *
 * ⚠️ TO ZATRZYMUJE AUTOMAT na czas procedury (agent wyłącza SPSUI, uruchamia `spsdbupdate.exe
 * -removereplication`, czyści tabele mechanizmu, usuwa programy kanału i podnosi interfejs),
 * dlatego ekran pyta o potwierdzenie. Odmowy pilnuje serwer TĄ SAMĄ bramą, która wyszarza
 * przycisk — inaczej mielibyśmy przycisk, który zawsze zawodzi, albo drogę wokół zabezpieczenia.
 */
export const wylaczSpsSync = (agentId: string, groupId: string) =>
  apiPost(`/agents/${agentId}/sps-sync-wylacz`, { groupId }, z.object({ id: z.string() }));

/**
 * WYŁĄCZENIE NATYWNEGO CHANGE TRACKINGU MSSQL-A NA WĘŹLE.
 *
 * ⚠️ W ODRÓŻNIENIU OD `wylaczSpsSync` TO NIE ZATRZYMUJE AUTOMATU: CT zdejmuje się per tabela,
 * jako zmiana metadanych. Potwierdzenie jest i tak, bo cudza integracja czytająca `CHANGETABLE`
 * padnie po tym bez śladu — a tego nie da się wykryć z bazy.
 */
export const wylaczCt = (agentId: string, groupId: string) =>
  apiPost(`/agents/${agentId}/ct-wylacz`, { groupId }, z.object({ id: z.string() }));

/**
 * PRZERWANIE WYŁĄCZANIA ŚLEDZENIA I PODNIESIENIE INTERFEJSU AUTOMATU — WYJŚCIE AWARYJNE.
 *
 * ⚠️⚠️ ZAMÓWIONE W TRAKCIE AWARII NA PRODUKCJI (09.09.2026): „musi być też bezwzględnie opcja
 * przerwania tego, która od razu uruchamia SPS, bo to się potrafi zapętlić jeśli się nie skończy
 * i teraz jest problem z wydawaniem w ogóle".
 *
 * ⚠️ JEDYNA TRASA TEJ RODZINY BEZ BRAMKI — działa też wtedy, gdy portal NIC nie widzi w toku.
 * To najgroźniejszy przypadek: agent zabity w środku zadania nie wykonał `finally` z startem
 * SPSUI, zadanie przeterminowało się w portalu, a automat stoi z zatrzymanym interfejsem.
 *
 * ⚠️ ZWRACA GOTOWE `zdanie` — mówi, co zrobiono i KIEDY interfejs wstanie. Agent bierze jedno
 * zadanie na raz, więc start wykona się w pierwszej wolnej chwili, a nie „od razu"; ekran nie ma
 * prawa obiecywać czegoś, czego kolejka nie dowozi.
 */
export const przerwijWylaczanieSledzenia = (agentId: string, groupId: string) =>
  apiPost(
    `/agents/${agentId}/przerwij-wylaczanie`,
    { groupId },
    z.object({ przerwanych: z.number(), startZlecony: z.boolean(), zdanie: z.string() }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  POCZEKALNIA — na które węzły kolejka NIE czeka.
 *
 *  ⚠️⚠️ ODŁOŻENIE TO NIE ODRZUCENIE. Węzeł zachowuje prawo zapisu i wszystkie zaległości; zmienia
 *  się wyłącznie to, czy reszta floty ma na niego czekać. Operacja config-down jest domknięta
 *  dopiero, gdy potwierdzą ją wszystkie węzły, więc jedna maszyna w serwisie trzyma kolejkę całej
 *  wyspy — a otwarta operacja zamraża wiersze i portal przestaje przyjmować stan od maszyn,
 *  które pracują.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const PoczekalniaSchema = z.object({
  wezly: z.array(
    z.object({
      id: z.string(),
      nodeId: z.string(),
      hostname: z.string().nullable(),
      lastSeenAt: z.string().nullable(),
      odlozonyOd: z.string().nullable(),
      wPoczekalni: z.boolean(),
      powod: z.enum(['RECZNIE', 'CISZA', 'NIGDY_NIE_ODPOWIEDZIAL', 'NADRABIA']).nullable(),
      minutOdKontaktu: z.number().nullable(),
      /** `null` = nie liczyliśmy, bo węzeł pracuje. */
      zalegaOperacji: z.number().nullable(),
    }),
  ),
  progMinut: z.number(),
});

export type PoczekalniaWyspy = z.infer<typeof PoczekalniaSchema>;

export const fetchPoczekalnia = (groupId: string) =>
  apiGet(`/agents/poczekalnia?groupId=${encodeURIComponent(groupId)}`, PoczekalniaSchema);

export const odlozWezel = (id: string, odloz: boolean) =>
  apiPost(`/agents/${id}/poczekalnia`, { odloz }, z.object({ id: z.string(), nodeId: z.string(), odlozony: z.boolean() }));
